import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { ExternalLink, MapPin, Briefcase } from "lucide-react";

/**
 * PlacementsDeepDiveCard — renders per-firm graduate-scheme + hub-
 * location cards on a DegreeDetail page, keyed off the degree's
 * country. Same visual pattern as the (now-superseded) Ireland-only
 * card, generalised to serve UK, Ireland and any future
 * `{country}_firm_hubs.py` seed we ship.
 *
 * Country routing is driven by REGION_CONFIGS below — each config
 * declares:
 *   • country_lc      lowercase country match (e.g. "ireland")
 *   • endpoint        backend firm-hubs endpoint
 *   • label           header text ("🇮🇪 Ireland placements deep-dive")
 *   • tint            accent colour palette (border/bg tokens)
 *
 * A degree with no matching region config renders `null`.
 * A degree whose `top_employers` don't intersect the region's hubs
 * dictionary also renders `null` (chip is opt-in signal).
 */

const REGION_CONFIGS = [
  {
    country_lc: "ireland",
    endpoint: "/ireland/firm-hubs",
    label: "🇮🇪 Ireland placements deep-dive",
    subtitle: "The firms this cohort pipes into — Dublin/Cork operations and grad scheme portals.",
    tint: {
      border: "border-emerald-200",
      bg: "bg-emerald-50/40",
      icon: "text-emerald-800",
      title: "text-emerald-900",
      body: "text-emerald-900/80",
      card_border: "border-emerald-200 hover:border-emerald-400",
      pill: "bg-emerald-100 hover:bg-emerald-200 text-emerald-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from firm careers pages and IDA Ireland site profiles.",
  },
  {
    country_lc: "united kingdom",
    endpoint: "/uk/firm-hubs",
    label: "🇬🇧 UK placements deep-dive",
    subtitle: "London EMEA hubs (with regional offices) and each firm's live graduate scheme portal.",
    tint: {
      border: "border-[#4C1D95]/25",
      bg: "bg-[#F5F0FF]",
      icon: "text-[#4C1D95]",
      title: "text-[#002147]",
      body: "text-[#002147]/70",
      card_border: "border-[#4C1D95]/20 hover:border-[#4C1D95]/70",
      pill: "bg-[#4C1D95]/10 hover:bg-[#4C1D95]/25 text-[#4C1D95]",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from firm careers pages, LinkedIn alumni queries and each firm's UK office directory.",
  },
  {
    country_lc: "italy",
    endpoint: "/italy/firm-hubs",
    label: "🇮🇹 Italy placements deep-dive",
    subtitle: "Milan finance hubs + luxury maisons + Rome consulting offices.",
    tint: {
      border: "border-rose-200",
      bg: "bg-rose-50/50",
      icon: "text-rose-800",
      title: "text-rose-900",
      body: "text-rose-900/70",
      card_border: "border-rose-200 hover:border-rose-400",
      pill: "bg-rose-100 hover:bg-rose-200 text-rose-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from Bocconi / Politecnico / Sapienza career reports and firm Milan office profiles.",
  },
  {
    country_lc: "france",
    endpoint: "/france/firm-hubs",
    label: "🇫🇷 France placements deep-dive",
    subtitle: "Paris finance + Sciences Po consulting pipelines + Toulouse/Vélizy aerospace.",
    tint: {
      border: "border-sky-200",
      bg: "bg-sky-50/50",
      icon: "text-sky-800",
      title: "text-sky-900",
      body: "text-sky-900/70",
      card_border: "border-sky-200 hover:border-sky-400",
      pill: "bg-sky-100 hover:bg-sky-200 text-sky-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from Sciences Po / HEC / Polytechnique career reports and firm Paris office directories.",
  },
  {
    country_lc: "germany",
    endpoint: "/germany/firm-hubs",
    label: "🇩🇪 Germany placements deep-dive",
    subtitle: "Frankfurt banking, München consulting + Bayern automotive/engineering giants.",
    tint: {
      border: "border-amber-200",
      bg: "bg-amber-50/60",
      icon: "text-amber-800",
      title: "text-amber-900",
      body: "text-amber-900/70",
      card_border: "border-amber-200 hover:border-amber-400",
      pill: "bg-amber-100 hover:bg-amber-200 text-amber-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from TUM/LMU/HU career reports, DACH consulting rankings, and firm office directories.",
  },
  {
    country_lc: "netherlands",
    endpoint: "/netherlands/firm-hubs",
    label: "🇳🇱 Netherlands placements deep-dive",
    subtitle: "Eindhoven Brainport chip-fab hub + Amsterdam Zuidas finance + Dutch trading houses.",
    tint: {
      border: "border-orange-200",
      bg: "bg-orange-50/50",
      icon: "text-orange-800",
      title: "text-orange-900",
      body: "text-orange-900/70",
      card_border: "border-orange-200 hover:border-orange-400",
      pill: "bg-orange-100 hover:bg-orange-200 text-orange-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from TU Delft / TU Eindhoven / Erasmus career reports and firm NL office profiles.",
  },
  {
    country_lc: "switzerland",
    endpoint: "/switzerland/firm-hubs",
    label: "🇨🇭 Switzerland placements deep-dive",
    subtitle: "Zürich Paradeplatz banks + Basel pharma titans + ETH-adjacent Google Zürich eng.",
    tint: {
      border: "border-red-200",
      bg: "bg-red-50/50",
      icon: "text-red-800",
      title: "text-red-900",
      body: "text-red-900/70",
      card_border: "border-red-200 hover:border-red-400",
      pill: "bg-red-100 hover:bg-red-200 text-red-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from ETH/EPFL career reports, Zürich finance directories and Basel pharma HR pages.",
  },
  {
    country_lc: "spain",
    endpoint: "/spain/firm-hubs",
    label: "🇪🇸 Spain placements deep-dive",
    subtitle: "Madrid banks + Cuatro Torres consulting + Barcelona tech + Bilbao renewables.",
    tint: {
      border: "border-yellow-300",
      bg: "bg-yellow-50/50",
      icon: "text-yellow-800",
      title: "text-yellow-900",
      body: "text-yellow-900/70",
      card_border: "border-yellow-300 hover:border-yellow-500",
      pill: "bg-yellow-100 hover:bg-yellow-200 text-yellow-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from IE / IESE / ESADE / UC3M / UPM career reports and firm Iberia office directories.",
  },
  {
    country_lc: "portugal",
    endpoint: "/portugal/firm-hubs",
    label: "🇵🇹 Portugal placements deep-dive",
    subtitle: "Lisbon banks + nearshore Google/Microsoft eng + Porto retail giants (Sonae, Farfetch).",
    tint: {
      border: "border-green-300",
      bg: "bg-green-50/50",
      icon: "text-green-800",
      title: "text-green-900",
      body: "text-green-900/70",
      card_border: "border-green-300 hover:border-green-500",
      pill: "bg-green-100 hover:bg-green-200 text-green-900",
    },
    attribution: "Curated by WhichUniversityAdvisor editors from Nova SBE / Católica / IST career reports and firm Iberia office directories.",
  },
];


function _matchRegion(degree) {
  // Prefer the explicit country field; fall back to the university_id
  // prefix (e.g. "uk-cambridge", "ie-tcd", "us-harvard") because a
  // large chunk of pre-Wave-2 UK degrees ship with country=null.
  const explicit = (degree?.country || "").trim().toLowerCase();
  const uidPrefix = (degree?.university_id || "").split("-")[0].toLowerCase();
  const uidToCountry = {
    uk: "united kingdom", ie: "ireland", us: "united states",
    it: "italy", fr: "france", de: "germany",
    nl: "netherlands", ch: "switzerland",
    es: "spain", pt: "portugal",
  };
  const c = explicit || uidToCountry[uidPrefix] || "";
  if (!c) return null;
  const isUK = ["uk", "united kingdom", "unitedkingdom", "great britain", "britain", "u.k."].includes(c);
  return REGION_CONFIGS.find((r) => {
    if (r.country_lc === "united kingdom") return isUK;
    return c === r.country_lc;
  }) || null;
}


export default function PlacementsDeepDiveCard({ degree }) {
  const region = _matchRegion(degree);
  const [hubs, setHubs] = useState(null);
  const sectionRef = useRef(null);
  // Two employer-list shapes coexist in the DB:
  //   • curated:   outcomes.destinations.top_employers (strings or {employer})
  //   • fallback:  outcomes.top_employers (plain strings)
  // Concatenating both catches every degree — Spanish/Portuguese
  // rows currently live only in the flat `outcomes.top_employers`.
  const _dst = ((degree?.outcomes?.destinations?.top_employers) || [])
    .map((e) => (typeof e === "string" ? e : e?.employer))
    .filter(Boolean);
  const _flat = ((degree?.outcomes?.top_employers) || [])
    .map((e) => (typeof e === "string" ? e : e?.employer))
    .filter(Boolean);
  const employers = _dst.length > 0 ? _dst : _flat;

  useEffect(() => {
    if (!region) return;
    if (employers.length === 0) return;
    let cancelled = false;
    api.get(region.endpoint).then((r) => {
      if (!cancelled) setHubs(r.data?.firms || {});
    }).catch(() => setHubs({}));
    return () => { cancelled = true; };
  }, [region?.endpoint, employers.length]);

  // React Router doesn't auto-scroll to `location.hash` when the
  // target section renders lazily (after our async firm-hubs fetch
  // above resolves), so we do the scroll ourselves once `hubs` is
  // loaded. Re-runs on hash change too so intra-page hash edits
  // (e.g. a compare chip click that lands on this page) also land
  // the viewport on the section.
  useEffect(() => {
    if (!hubs || !sectionRef.current) return;
    if (typeof window === "undefined") return;
    if (window.location.hash !== "#placements-deep-dive") return;
    // rAF x2 so the browser has a chance to paint the mounted card
    // before we ask it to scroll — feels smoother than a raw call.
    const raf = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        sectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [hubs]);

  if (!region || employers.length === 0 || !hubs) return null;

  const matched = employers
    .map((name) => ({ name, hub: _resolveHub(name, hubs) }))
    .filter((r) => r.hub);
  if (matched.length === 0) return null;

  const t = region.tint;
  return (
    <section
      ref={sectionRef}
      id="placements-deep-dive"
      className={`mt-6 border ${t.border} ${t.bg} rounded-lg p-5 scroll-mt-20`}
      data-testid={`placements-deep-dive-card-${region.country_lc.replace(/\s+/g, "-")}`}
    >
      <div className="flex items-center gap-2 mb-3">
        <Briefcase className={`h-4 w-4 ${t.icon}`} aria-hidden />
        <div className={`label-uppercase !text-[10px] ${t.title}`}>
          {region.label}
        </div>
      </div>
      <div className={`text-sm ${t.body} mb-4 leading-relaxed`}>
        {region.subtitle}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {matched.map(({ name, hub }) => (
          <FirmHubCard key={name} name={name} hub={hub} tint={t} />
        ))}
      </div>
      <div className={`mt-3 text-[10px] ${t.body} italic`}>
        {region.attribution}
      </div>
    </section>
  );
}


function FirmHubCard({ name, hub, tint }) {
  return (
    <div
      className={`rounded-md border ${tint.card_border} bg-white p-3 transition`}
      data-testid={`firm-hub-${_slug(name)}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-stone-900 text-sm">{name}</div>
          <div className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-stone-500">
            <MapPin className="h-3 w-3" aria-hidden />
            {hub.location}
          </div>
        </div>
        {hub.grad_url && (
          <a
            href={hub.grad_url}
            target="_blank"
            rel="noopener noreferrer"
            className={`inline-flex items-center gap-1 rounded-full ${tint.pill} text-[11px] font-mono px-2 py-1 whitespace-nowrap`}
            title={`${name} graduate scheme`}
            data-testid={`firm-grad-link-${_slug(name)}`}
          >
            Grad scheme
            <ExternalLink className="h-3 w-3" aria-hidden />
          </a>
        )}
      </div>
      <div className="mt-2 text-[12px] text-stone-700 leading-snug">
        {hub.hub}
      </div>
    </div>
  );
}


function _slug(s) {
  return String(s || "").toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}


// Strip accents / diacritics + drop generic corporate suffixes so
// "Deloitte España" or "Airbus España" match "Deloitte" / "Airbus"
// entries in the hub dictionary. Also drops leading "Banco " so
// "Banco Santander" matches "Santander" (and vice versa).
function _normalizeFirm(s) {
  return String(s || "")
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\b(espana|iberia|iberica|portugal|group|grupo|group\.|holding|holdings|international|co\.|s\.a\.|s\.l\.|plc|inc\.|ltd|limited|corporation|corp|company|europe|emea)\b/gi, " ")
    .replace(/^banco\s+/, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}


// Try exact then normalized then word-boundary match against the
// region's hub keys — Spanish/Portuguese destinations often carry a
// suffix ("Deloitte España") that would break naïve equality.
function _resolveHub(name, hubs) {
  if (hubs[name]) return hubs[name];
  const norm = _normalizeFirm(name);
  if (!norm || norm.length < 3) return null;
  const nameWords = new Set(norm.split(/\s+/).filter((w) => w.length >= 3));
  for (const [k, v] of Object.entries(hubs)) {
    const nk = _normalizeFirm(k);
    if (!nk) continue;
    if (nk === norm) return v;
    // Word-boundary check: every word of the hub key must appear as a
    // whole word in the employer name (or vice versa). Prevents SAP
    // matching sapient etc.
    const kWords = nk.split(/\s+/).filter((w) => w.length >= 3);
    if (kWords.length === 0) continue;
    const allKInName = kWords.every((w) => nameWords.has(w));
    if (allKInName) return v;
  }
  return null;
}
